import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { prequalApi } from "../../api";
import { ProcError, ProcForm, ProcSection } from "../../components/ProcurementLayout";
import { ProcStatusBadge } from "../../components/ProcStatusBadge";
import { useMutation } from "../../hooks";

const CHECKLIST_ITEMS = [
  { key: "certOfIncorp", label: "Certificate of Incorporation" },
  { key: "kraCompliance", label: "KRA Tax Compliance Certificate" },
  { key: "businessPermit", label: "Business Permit" },
  { key: "companyProfile", label: "Company Profile" },
  { key: "pinVatCert", label: "PIN/VAT Certificate" },
  { key: "signedApplication", label: "Signed Application Form" },
  { key: "cr12", label: "CR12 Form" },
] as const;

const TECH_ROWS = [
  { key: "techCompanyExperience", label: "Company Experience" },
  { key: "techFinancialCapability", label: "Financial Capability" },
  { key: "techReferences", label: "References" },
  { key: "techTechnicalCapacity", label: "Technical Capacity" },
  { key: "techCertifications", label: "Certifications" },
] as const;

type CheckKey = typeof CHECKLIST_ITEMS[number]["key"];
type TechKey = typeof TECH_ROWS[number]["key"];

export default function PrequalCreatePage() {
  const navigate = useNavigate();

  const [supplierName, setSupplierName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [checklist, setChecklist] = useState<Record<CheckKey, boolean>>({
    certOfIncorp: false,
    kraCompliance: false,
    businessPermit: false,
    companyProfile: false,
    pinVatCert: false,
    signedApplication: false,
    cr12: false,
  });

  const [techScores, setTechScores] = useState<Record<TechKey, number>>({
    techCompanyExperience: 0,
    techFinancialCapability: 0,
    techReferences: 0,
    techTechnicalCapacity: 0,
    techCertifications: 0,
  });

  const responsive = Object.values(checklist).every(Boolean);
  const techTotal = Object.values(techScores).reduce((s, v) => s + v, 0);
  const passed = techTotal >= 70;

  const { mutate, loading, error } = useMutation(async () => {
    if (!supplierName.trim()) throw new Error("Supplier name is required.");
    const result = await prequalApi.create({
      supplierName,
      contactPerson,
      phone,
      email,
      ...checklist,
      responsive,
      ...techScores,
    });
    navigate(`/procurement/supplier-prequal/${result.id}`);
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/procurement/supplier-prequal" className="text-sm text-emerald-700 hover:underline">&larr; Back to Prequalifications</Link>
      </div>

      <ProcForm formNo="PROC-FRM-07" title="Supplier Prequalification">
        {error && <ProcError message={error} />}

        <ProcSection title="Supplier Information">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Supplier Name *</label>
              <input type="text" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" required />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Contact Person</label>
              <input type="text" value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Phone</label>
              <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl border px-3 py-2 text-sm" />
            </div>
          </div>
        </ProcSection>

        <ProcSection title="Mandatory Checklist">
          <div className="space-y-2">
            {CHECKLIST_ITEMS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checklist[key]}
                  onChange={(e) => setChecklist((prev) => ({ ...prev, [key]: e.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                />
                <span className="text-sm text-slate-700">{label}</span>
              </label>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-700">Responsive:</span>
            <ProcStatusBadge value={responsive ? "YES" : "NO"} />
          </div>
        </ProcSection>

        <ProcSection title="Technical Scoring">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Criterion</th>
                <th className="px-3 py-2 text-right w-32">Score (0–20)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {TECH_ROWS.map(({ key, label }) => (
                <tr key={key}>
                  <td className="px-3 py-2 text-slate-700">{label}</td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      value={techScores[key]}
                      min={0}
                      max={20}
                      onChange={(e) => setTechScores((prev) => ({ ...prev, [key]: Math.min(20, Math.max(0, Number(e.target.value))) }))}
                      className="w-full rounded border px-2 py-1 text-sm text-right"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-bold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{techTotal} / 100</td>
              </tr>
            </tfoot>
          </table>
          <div className="mt-3 flex items-center gap-3">
            <span className="text-sm font-semibold text-slate-700">Pass mark: 70 —</span>
            <ProcStatusBadge value={passed ? "PASSED" : "FAILED"} />
          </div>
        </ProcSection>

        <div className="flex justify-end gap-3 pt-2">
          <Link to="/procurement/supplier-prequal" className="rounded-xl border px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</Link>
          <button type="button" onClick={() => mutate()} disabled={loading} className="rounded-xl bg-emerald-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-60">
            {loading ? "Saving…" : "Save Prequalification"}
          </button>
        </div>
      </ProcForm>
    </div>
  );
}
